import { EumSearchBase, SearchState, FieldConfig } from '../eum-search-base.js';
import { bootstrapSheet } from '../core/styles/bootstrap.js';
import { eumBaseSheet } from '../core/styles/eum-base.js';
import { icon, hasIcon } from '../core/icons/icons.js';
import { ModalManager } from '../core/utils/modal-manager.js';
import { PromptManager } from '../core/utils/prompt-manager.js';
import { UserService } from '../core/services/user-service.js';
import { GroupService } from '../core/services/group-service.js';
import { directoryRules, DirectoryCapabilities, DirectoryRuleContext } from './directory-rules.js';
import { EumUserToolbar, UserToolbarHost, UserToolbarCapabilities } from '../shared/eum-user-toolbar/eum-user-toolbar.js';
// @ts-ignore — handled by esbuild inline-css plugin
import componentSheet from './eum-member-directory.css';
// @ts-ignore — handled by esbuild inline-css plugin
import toolbarSheet from '../shared/eum-user-toolbar/eum-user-toolbar.css';
import '../shared/eum-search-box/eum-search-box.js';
import '../shared/eum-pagination/eum-pagination.js';
import '../shared/eum-results-meta/eum-results-meta.js';
import '../shared/eum-facet-panel/eum-facet-panel.js';
import '../shared/eum-person-avatar/eum-person-avatar.js';
import '../shared/eum-view-switcher/eum-view-switcher.js';

/** The views the directory can render results in. */
type DirectoryView = 'cards' | 'grid' | 'table';

/** Visual density of the people list. */
type Density = 'comfortable' | 'compact';

/** Where a field can be surfaced. */
type FieldPlacement = 'cards' | 'grid' | 'table' | 'details'; 

/** 
 * The semantic role a field plays. Roles let a single unified `fields` entry
 * drive avatar/name/subtitle/link rendering without duplicate config arrays.
 *  - name:        the person's display name (avatar initials + heading).
 *  - subtitle:    secondary line under the name (e.g. jobTitle • department).
 *  - avatar-id:   the id used to resolve the profile image URL.
 *  - avatar-flag: boolean-ish field indicating whether an image exists.
 *  - profile-url: value used to build the "View Profile" link (Slug/id).
 */
type FieldRole = 'name' | 'subtitle' | 'avatar-id' | 'avatar-flag' | 'profile-url';

/** How a field's value should be rendered as a link/icon. */
type FieldType = 'text' | 'email' | 'phone' | 'link' | 'chat';

/**
 * What happens when a result (card/column/row) is clicked.
 *  - details: open the in-place details modal (default).
 *  - profile: navigate straight to the person's profile page.
 *  - none:    results are not clickable.
 */
type ResultClick = 'details' | 'profile' | 'none';

/**
 * How the details modal presents its field list — the modern replacement for
 * the legacy `resultTargets` simple/full distinction.
 *  - toggle: open collapsed (simple) with a "More Details" button to expand (default).
 *  - full:   open expanded with the full field list; no toggle button.
 *  - simple: never show the full field list or the toggle (identity + icons only).
 */
type DetailsMode = 'toggle' | 'full' | 'simple';

/** All targets a result can be surfaced in, including the details modal. */
const ALL_TARGETS: FieldPlacement[] = ['cards', 'grid', 'table', 'details'];

/**
 * Unified field configuration for the member directory. Extends the base
 * FieldConfig with placement (`in`), ordering (`order`), semantic `role`,
 * rendering `type`, and an optional shared-library `icon` name.
 */
export interface MemberFieldConfig extends FieldConfig {
    in?: FieldPlacement[] | string;
    order?: number;
    role?: FieldRole;
    type?: FieldType;
    icon?: string;
}

/**
 * Admin-only editable field configuration. Declared in a top-level `adminFields`
 * array (a sibling of `fields`) because editing is a distinct, admin-gated
 * feature set. `key` is the payload property PATCHed to `users/{id}`; `label`
 * is the form label. For now every input is treated as text.
 */
export interface AdminFieldConfig {
    key: string;
    label: string;
    order?: number;
    /** Optional read-only flag — rendered but disabled in the form. */
    readonly?: boolean | string;
}

const ALL_VIEWS: DirectoryView[] = ['cards', 'grid', 'table'];
const ALL_DENSITIES: Density[] = ['comfortable', 'compact'];

export class EumMemberDirectory extends EumSearchBase {
    private shadow!: ShadowRoot;
    private resultsEl!: HTMLElement;
    private loadingEl!: HTMLElement;
    private noResultsEl!: HTMLElement;
    private noResultsMessage!: HTMLElement;
    private paginationEl!: HTMLElement | null;
    private resultsMetaEl!: HTMLElement | null;
    private viewSwitcherEl!: HTMLElement | null;
    private detailsModalEl!: HTMLElement | null;
    private editModalEl!: HTMLElement | null;
    private confirmModalEl!: HTMLElement | null;
    private modals!: ModalManager;
    private prompts!: PromptManager;

    private siteUrl = '';
    private profileUrl = '';
    private currentResults: Array<{ document: Record<string, any> }> = [];

    // Admin editing state.
    private userService!: UserService;
    private groupService!: GroupService;
    private groupId = '';
    private isAdmin = false;
    private groupManageable = false;
    /** Group ids resolved from a SharePoint site URL or the all-groups scope. */
    private scopedGroupIds: string[] = [];
    /** The shared user-management toolbar (bulk actions), when enabled. */
    private toolbar: EumUserToolbar | null = null;
    private toolbarEl: HTMLElement | null = null;
    private capabilities: DirectoryCapabilities = directoryRules.capabilities<DirectoryRuleContext>({
        isAdmin: false,
        groupManageable: false,
    });
    /** Full user record currently loaded into the edit modal. */
    private editingUser: Record<string, any> | null = null;
    /** Base64 (header-stripped) of a newly selected avatar, if any. */
    private pendingAvatarBase64 = '';

    protected attachShadowRoot(): void {
        this.shadow = this.attachShadow({ mode: 'open' });
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.modals?.destroy();
        this.prompts?.destroy();
    }

    // ── View helpers ────────────────────────────────────────────────────

    /** Normalize a raw view/placement token, mapping the legacy `columns` alias to `grid`. */
    private normalizeView(v: string): string {
        return v === 'columns' ? 'grid' : v;
    }

    private get currentView(): DirectoryView {
        const v = this.normalizeView(String(this.store.proxy.view)) as DirectoryView;
        const enabled = this.enabledViews;
        if (enabled.includes(v)) return v;
        // Fall back to the configured default when set and enabled, else the
        // first enabled view.
        const configured = this.normalizeView(String((this.config as any).view)) as DirectoryView;
        if (enabled.includes(configured)) return configured;
        return enabled[0];
    }

    /**
     * The views the switcher exposes, from the config `views` array (e.g.
     * `["cards", "grid"]`). A comma-separated string is also accepted. The
     * legacy `columns` alias maps to `grid`. Unknown entries are ignored; an
     * empty/absent value enables all views.
     */
    private get enabledViews(): DirectoryView[] {
        const raw = (this.config as any).views;
        const list: string[] | null = Array.isArray(raw)
            ? raw
            : typeof raw === 'string' && raw.trim()
                ? raw.split(',').map((v) => v.trim())
                : null;
        if (!list) return ALL_VIEWS;
        const filtered = list
            .map((v) => this.normalizeView(v))
            .filter((v): v is DirectoryView => ALL_VIEWS.includes(v as DirectoryView));
        return filtered.length ? filtered : ALL_VIEWS;
    }

    /**
     * The visual density for the current view, from the config `density`
     * property. Accepts either a single token (`"compact"`) applied to all
     * views, or a per-view map (`{ "grid": "compact" }`). Falls back to
     * `comfortable` when unset or unrecognised.
     */
    private get density(): Density {
        const raw = (this.config as any).density;
        if (raw == null) return 'comfortable';
        let value: unknown = raw;
        if (typeof raw === 'object') {
            value = raw[this.currentView] ?? raw['all'];
        }
        const token = String(value).trim().toLowerCase() as Density;
        return ALL_DENSITIES.includes(token) ? token : 'comfortable';
    }

    /**
     * The set of enabled result targets, from the config `targets` array (e.g.
     * `["cards", "grid", "details"]`). A comma-separated string is also
     * accepted. This is the higher-level switch that turns an entire target on
     * or off, independent of the per-field `in` placement: a field only renders
     * in a target when that target is enabled *and* the field opts into it via
     * `in`. The legacy `columns` alias maps to `grid`. An empty/absent value
     * enables all targets.
     */
    private get enabledTargets(): FieldPlacement[] {
        const raw = (this.config as any).targets;
        const list: string[] | null = Array.isArray(raw)
            ? raw
            : typeof raw === 'string' && raw.trim()
                ? raw.split(',').map((v) => v.trim())
                : null;
        if (!list) return ALL_TARGETS;
        const filtered = list
            .map((v) => this.normalizeView(v))
            .filter((v): v is FieldPlacement => ALL_TARGETS.includes(v as FieldPlacement));
        return filtered.length ? filtered : ALL_TARGETS;
    }

    /** True when the given target is enabled at the higher (target) level. */
    private targetEnabled(placement: FieldPlacement): boolean {
        return this.enabledTargets.includes(placement);
    }

    /**
     * What happens when a result is clicked, from the config `resultClick`
     * property. Accepts `details` (open the details modal — the default),
     * `profile` (navigate straight to the profile page), or `none`. When the
     * `details` target is disabled via `targets`, clicking falls back to
     * `profile` so results remain actionable.
     */
    private get resultClick(): ResultClick {
        const raw = String((this.config as any).resultClick ?? '').trim().toLowerCase();
        if (raw === 'profile' || raw === 'none' || raw === 'details') return raw as ResultClick;
        // No explicit setting: default to details when that target is enabled,
        // otherwise send the click straight to the profile page.
        return this.targetEnabled('details') ? 'details' : 'profile';
    }

    /**
     * How the details modal presents its full field list, from the config
     * `detailsMode` property — the modern replacement for the legacy
     * `resultTargets` simple/full distinction.
     *  - toggle: open collapsed with a "More Details" button to expand (default).
     *  - full:   open expanded with the full field list; no toggle button.
     *  - simple: never render the full field list or the toggle (identity + icons only).
     */
    private get detailsMode(): DetailsMode {
        const raw = String((this.config as any).detailsMode ?? '').trim().toLowerCase();
        return raw === 'full' || raw === 'simple' || raw === 'toggle'
            ? (raw as DetailsMode)
            : 'toggle';
    }

    /**
     * True when the directory is running on an anonymous/public page.
     * mode skips all admin capability probes (no `getAccessLevel` call) so the
     * component never attempts authenticated requests, and it is the explicit
     * replacement for the legacy `userIsAuthenticated` gate.
     */
    private get isAnonymous(): boolean {
        return this.checkBoolean((this.config as any).anonymous, false);
    }

    // ── Fields ──────────────────────────────────────────────────────────

    private get memberFields(): MemberFieldConfig[] {
        return ((this.config.fields || []) as MemberFieldConfig[]).map((field) => ({
            ...field,
            label: this.t(field.label ?? ''),
        }));
    }

    /** Admin-only editable fields, ordered by `order` then declaration index. */
    private get adminFields(): AdminFieldConfig[] {
        const raw = (this.config as any).adminFields;
        if (!Array.isArray(raw)) return [];
        return (raw as AdminFieldConfig[])
            .map((field, index) => ({ field, index }))
            .sort((a, b) => {
                const orderA = a.field.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.field.order ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB || a.index - b.index;
            })
            .map(({ field }) => ({ ...field, label: this.t(field.label ?? '') }));
    }

    /** Fields visible in a given placement, sorted by `order` then original index. */
    private fieldsFor(placement: FieldPlacement): MemberFieldConfig[] {
        return this.memberFields
            .map((field, index) => ({ field, index }))
            .filter(({ field }) => this.isVisible(field) && this.appliesTo(field, placement))
            .sort((a, b) => {
                const orderA = a.field.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.field.order ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB || a.index - b.index;
            })
            .map(({ field }) => field);
    }

    private isVisible(field: MemberFieldConfig): boolean {
        return field.visible === true || field.visible === 'true';
    }

    private appliesTo(field: MemberFieldConfig, placement: FieldPlacement): boolean {
        // A disabled target shows no fields at all, regardless of per-field `in`.
        if (!this.targetEnabled(placement)) return false;
        const inSpec = field.in;
        if (!inSpec) return true; // no placement restriction → applies everywhere
        const placements = (Array.isArray(inSpec)
            ? inSpec
            : String(inSpec).split(',').map((p) => p.trim())
        ).map((p) => this.normalizeView(p));
        return placements.includes(placement);
    }

    private fieldByRole(role: FieldRole): MemberFieldConfig | undefined {
        return this.memberFields.find((f) => f.role === role);
    }

    /**
     * Optional per-placement grid column count from config. When set it
     * overrides the CSS `--grid-column-count` for that grid. Accepts either a
     * `columns` map keyed by placement (`{ "cards": 5, "details": 2 }`) or a
     * single numeric `columns` value applied to all grids. The legacy
     * `columns` placement key maps to `grid`.
     */
    private columnCount(placement: FieldPlacement): number | null {
        const raw = (this.config as any).columns;
        if (raw == null) return null;
        let value: unknown = raw;
        if (typeof raw === 'object') {
            value = raw[placement];
            // Honour a legacy `columns` key for the `grid` placement.
            if (value == null && placement === 'grid') value = raw.columns;
        }
        const n = parseInt(String(value), 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    }

    /** Inline `style` attribute setting `--grid-column-count`, or '' when unset. */
    private columnCountStyle(placement: FieldPlacement): string {
        const n = this.columnCount(placement);
        return n ? ` style="--grid-column-count: ${n}"` : '';
    }

    // ── Render ──────────────────────────────────────────────────────────

    protected render(): void {
        const showSearchbox = this.checkBoolean(this.config.showSearchbox);
        const showResultsMetadata = this.checkBoolean(this.config.showResultsMetadata);
        const showPagination = this.checkBoolean(this.config.showPagination);
        const showFacets = this.checkBoolean(this.config.showFacets, false);
        const showViews = this.checkBoolean((this.config as any).showViews, true);

        this.shadow.innerHTML = /*html*/ `
            <div class="eum-member-directory">
                <slot name="title"></slot>

                <div class="directory-body${showFacets ? ' has-facets' : ''}">
                    ${showFacets ? '<eum-facet-panel class="facet-sidebar"></eum-facet-panel>' : ''}

                    <div class="results-area d-flex flex-column gap-1">

                        <div class="d-flex flex-column gap-1">
                            <!-- The bulk-actions toolbar is injected above the search box
                                 only when the resolved capabilities authorise it (see
                                 mountToolbar). It is intentionally absent from the base
                                 template so unauthorised users never receive the markup. -->
                            <div class="directory-toolbar flex-md-row align-items-md-start">
                                <div class="flex-grow-1 d-flex flex-column">
                                    ${showSearchbox ? '<eum-search-box></eum-search-box>' : ''}
                                </div>
                                ${showViews ? `<eum-view-switcher views="${this.enabledViews.join(',')}" current="${this.currentView}"></eum-view-switcher>` : ''}
                            </div>

                            <div class="loading text-center py-4" hidden>
                                <div class="d-flex align-items-center justify-content-center">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="visually-hidden">${this.escapeHTML(this.t('memberDirectory.search.loading.status'))}</span>
                                    </div>
                                    <span class="ms-2 text-body-secondary">${this.escapeHTML(this.t('memberDirectory.search.loading.message'))}</span>
                                </div>
                            </div>

                            ${showResultsMetadata ? '<eum-results-meta hidden></eum-results-meta>' : ''}
                        </div>

                        <div class="no-results text-center py-4 text-body-secondary" hidden>
                            <div class="d-flex flex-column gap-3 align-items-center">
                                <div class="search-noresults-icon">
                                    <svg width="48" height="48" viewBox="0 0 20 20" fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M15.5 4.8c2 3 1.7 7-1 9.7h0l4.3 4.3-4.3-4.3a7.8 7.8 0 01-9.8 1m-2.2-2.2A7.8 7.8 0 0113.2 2.4M2 18L18 2"></path>
                                    </svg>
                                </div>
                                <p class="no-results-message mb-0">${this.escapeHTML(this.t('memberDirectory.results.empty.message'))}</p>
                            </div>
                        </div>

                        <div class="d-flex flex-column gap-3">
                            <div class="search-results density-${this.density}" hidden></div>
                            ${showPagination ? '<eum-pagination hidden></eum-pagination>' : ''}
                        </div>

                    </div>
                </div>

                <div class="modal fade member-details-modal" role="dialog" tabindex="-1" aria-hidden="true">
                    <div class="modal-dialog modal-dialog-centered" role="document">
                        <div class="modal-content">
                            <div class="modal-header">
                                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${this.escapeAttr(this.t('memberDirectory.detailsModal.closeLabel'))}"></button>
                            </div>
                            <div class="modal-body">
                                <div class="details-layout">
                                    <eum-person-avatar class="details-avatar"></eum-person-avatar>
                                    <div class="details-main">
                                        <div class="details-identity">
                                            <h5 class="details-name mb-0"></h5>
                                            <div class="details-subtitle text-body-secondary"></div>
                                            <div class="details-icons icons"></div>
                                        </div>
                                        <div class="details-content" hidden>
                                            <h6 class="details-fields-heading">${this.escapeHTML(this.t('memberDirectory.detailsModal.contactHeading'))}</h6>
                                            <div class="details-fields"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="modal-footer">
                                <!-- The admin "Edit User" button is injected here only when the
                                     current user is authorised (see maybeInjectAdminUi). -->
                                <a class="details-profile-link btn" target="_blank" rel="noopener" hidden></a>
                                <button type="button" class="details-more btn" hidden></button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Admin-only modals (edit + confirm) are injected after the
                     capability check resolves. They are intentionally absent from
                     the base template so unauthorised users never receive the
                     markup at all. -->
            </div>
        `;

        this.resultsEl = this.shadow.querySelector('.search-results')!;
        this.loadingEl = this.shadow.querySelector('.loading')!;
        this.noResultsEl = this.shadow.querySelector('.no-results')!;
        this.noResultsMessage = this.shadow.querySelector('.no-results-message')!;
        this.paginationEl = this.shadow.querySelector('eum-pagination');
        this.resultsMetaEl = this.shadow.querySelector('eum-results-meta');
        this.viewSwitcherEl = this.shadow.querySelector('eum-view-switcher');
        this.detailsModalEl = this.shadow.querySelector('.member-details-modal');
        // The bulk-actions toolbar is created on demand by mountToolbar() once
        // the capability check authorises it; its mount point doesn't exist yet.
        this.toolbarEl = null;
        // Admin modals (edit + confirm) are created on demand once the
        // capability check authorises them; they don't exist yet.
        this.editModalEl = null;
        this.confirmModalEl = null;
    }

    protected async onInit(): Promise<void> {
        this.shadow.adoptedStyleSheets = [bootstrapSheet, eumBaseSheet, toolbarSheet, componentSheet];
        this.initCommonEvents(this.shadow);

        this.siteUrl = String((this.config as any).siteUrl ?? '');
        this.profileUrl = String((this.config as any).profileUrl ?? (this.config as any).detailsPagePath ?? '');

        this.shadow.addEventListener('eum-view-change', ((e: CustomEvent) => {
            const view = e.detail.view as DirectoryView;
            if (!ALL_VIEWS.includes(view)) return;
            this.store.proxy.view = view;
        }) as EventListener);

        this.modals = new ModalManager(this.shadow);
        this.prompts = new PromptManager(this.shadow);
        if (this.detailsModalEl) this.modals.register(this.detailsModalEl);

        // Admin editing: wire the API service and resolve capabilities up-front.
        // The admin UI (edit/confirm modals, edit + remove controls) is only
        // injected into the DOM when the resolved capabilities allow it, so an
        // unauthorised user never receives that markup.
        this.userService = new UserService(String((this.config as any).apiBaseUrl ?? ''));
        this.groupService = new GroupService(String((this.config as any).apiBaseUrl ?? ''));
        this.groupId = String((this.config as any).groupId ?? (this.config as any).groupid ?? '');

        await this.resolveCapabilities();

        // Resolve any SharePoint-site / all-groups scope into concrete group ids
        // before scoping the filter (mirrors the legacy directory's site/all-
        // groups modes). A single explicit `groupId` takes precedence.
        await this.resolveGroupScope();

        // Scope results to the target group(s) by folding a MemberGroups clause
        // into the base filter (matches eum-user-management's group scoping).
        this.applyGroupFilter();

        this.maybeInjectAdminUi();
        this.mountToolbar();

        const hasQueryParams = this.applyQueryParams();

        if (hasQueryParams) {
            this.search();
        } else if (this.checkBoolean(this.config.allowEmptySearch, false)) {
            this.search('');
        }
    }

    protected onStateChange(state: SearchState, changedKey: keyof SearchState): void {
        switch (changedKey) {
            case 'loading':
                this.loadingEl.hidden = !state.loading;
                this.noResultsEl.hidden = state.loading || state.hasResults;
                this.resultsEl.hidden = state.loading || !state.hasResults;
                if (this.paginationEl) this.paginationEl.hidden = state.loading || !state.hasResults;
                if (this.resultsMetaEl) this.resultsMetaEl.hidden = state.loading || !state.hasResults;
                break;

            case 'view':
                this.renderResults(state);
                if (this.viewSwitcherEl) {
                    this.viewSwitcherEl.setAttribute('current', this.currentView);
                }
                break;

            case 'results':
            case 'hasResults':
                this.renderResults(state);
                this.resultsEl.hidden = !state.hasResults;
                this.noResultsEl.hidden = !state.hasSearched || state.hasResults;
                if (this.paginationEl) this.paginationEl.hidden = !state.hasResults;
                if (this.resultsMetaEl) this.resultsMetaEl.hidden = !state.hasResults;
                this.toolbar?.updateState(state.hasResults);
                if (!state.hasResults && state.hasSearched) {
                    const searchText = state.searchText;
                    this.noResultsMessage.textContent = searchText
                        ? this.t('memberDirectory.results.empty.messageForTerm', { term: searchText })
                        : this.t('memberDirectory.results.empty.message');
                }
                break;

            case 'totalCount':
            case 'fetchTime': {
                if (this.resultsMetaEl) {
                    this.resultsMetaEl.hidden = !state.hasSearched || !state.hasResults;
                    this.resultsMetaEl.setAttribute('total-count', String(state.totalCount));
                    this.resultsMetaEl.setAttribute('fetch-time', state.fetchTime ?? '');
                }
                if (this.paginationEl) {
                    this.paginationEl.setAttribute('total-count', String(state.totalCount));
                    this.paginationEl.setAttribute('page-size', String(this.options.searchOptions.size ?? 25));
                    this.paginationEl.setAttribute('current-page', String(state.currentPage));
                }
                break;
            }

            case 'currentPage': {
                if (this.paginationEl) {
                    this.paginationEl.setAttribute('current-page', String(state.currentPage));
                }
                break;
            }

            case 'facetData': {
                const facetPanel = this.shadow.querySelector('eum-facet-panel');
                if (facetPanel) {
                    facetPanel.setAttribute('data-facets', JSON.stringify(state.facetData));
                }
                break;
            }
        }
    }

    // ── Result rendering ────────────────────────────────────────────────

    private renderResults(state: SearchState): void {
        this.currentResults = state.results;

        if (!state.hasResults) {
            this.resultsEl.innerHTML = '';
            return;
        }

        this.resultsEl.innerHTML =
            this.currentView === 'table'
                ? this.renderTable(state)
                : this.renderPeopleList(state);

        this.bindResultClicks();
    }

    /** Wire click-to-open-details on rendered cards, columns, and table rows. */
    private bindResultClicks(): void {
        const behavior = this.resultClick;
        if (behavior === 'none') return;

        this.resultsEl.querySelectorAll<HTMLElement>('[data-member-index]').forEach((el) => {
            el.addEventListener('click', (e) => {
                // Let real links/buttons behave normally.
                if ((e.target as HTMLElement).closest('a, button')) return;
                const index = Number(el.dataset.memberIndex);
                const item = this.currentResults[index];
                if (!item) return;
                if (behavior === 'profile') {
                    this.goToProfile(item.document);
                } else {
                    this.openDetailsModal(item.document);
                }
            });
        });
    }

    /** Navigate straight to a person's profile page, when a URL can be built. */
    private goToProfile(doc: Record<string, any>): void {
        const url = this.buildProfileUrl(doc);
        if (url) window.location.href = url;
    }

    private renderTable(state: SearchState): string {
        const fields = this.fieldsFor('table');
        const head = fields.map((f) => `<th>${this.escapeHTML(f.label)}</th>`).join('');
        const body = state.results
            .map((item, index) => {
                const cells = fields
                    .map((f) => `<td>${this.renderFieldValue(f, item.document, /*withIcon*/ false)}</td>`)
                    .join('');
                return `<tr class="member-row" data-member-index="${index}">${cells}</tr>`;
            })
            .join('');

        return /*html*/ `
            <div class="table-responsive">
                <table class="table table-striped table-hover align-middle mb-0">
                    <thead><tr>${head}</tr></thead>
                    <tbody>${body}</tbody>
                </table>
            </div>`;
    }

    private renderPeopleList(state: SearchState): string {
        const view = this.currentView; // 'cards' | 'grid'
        const cards = state.results.map((item, index) => this.renderPersonCard(item.document, view, index)).join('');
        return `<div class="people-list ${view}"${this.columnCountStyle(view)}>${cards}</div>`;
    }

    private renderPersonCard(doc: Record<string, any>, view: DirectoryView, index: number): string {
        const name = this.roleValue('name', doc);
        const subtitles = this.subtitleValues(doc);
        const avatarId = this.roleValue('avatar-id', doc);
        const hasImage = this.hasImageValue(doc);
        const imageUrl = hasImage && avatarId ? this.buildImageUrl(avatarId) : '';

        const iconFields = this.fieldsFor(view).filter((f) => f.type && f.type !== 'text');

        let icons = null;

        switch (this.currentView) {
            case 'cards':
                icons = iconFields
                    .map((f) => this.renderIcon(f, doc))
                    .filter(Boolean)
                    .join('');
                break;
        }

        const subtitleMarkup = subtitles
            .map((s) => `<div class="job-information">${this.escapeHTML(s)}</div>`)
            .join('');

        return /*html*/ `
            <div class="person" data-member-index="${index}">
                <div class="clickable-area"></div>
                <eum-person-avatar
                    display-name="${this.escapeAttr(name)}"
                    has-image="${hasImage ? 'true' : 'false'}"
                    ${imageUrl ? `image-url="${this.escapeAttr(imageUrl)}"` : ''}></eum-person-avatar>
                <div class="details">
                    <h6 class="display-name" title="${this.escapeAttr(name)}">${this.escapeHTML(name)}</h6>
                    ${subtitleMarkup}
                    ${icons ? `<div class="icons">${icons}</div>` : ''} 
                </div>
            </div>`;
    }

    // ── Field value helpers ─────────────────────────────────────────────

    private roleValue(role: FieldRole, doc: Record<string, any>): string {
        const field = this.fieldByRole(role);
        if (!field) return '';
        return String(doc[field.key] ?? '');
    }

    /** Subtitle line values, one per `role: subtitle` field, ordered by `order`. */
    private subtitleValues(doc: Record<string, any>): string[] {
        // Support multiple `role: subtitle` fields, each rendered on its own line.
        const subtitleFields = this.memberFields
            .map((field, index) => ({ field, index }))
            .filter(({ field }) => field.role === 'subtitle' && this.isVisible(field))
            .sort((a, b) => {
                const orderA = a.field.order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.field.order ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB || a.index - b.index;
            })
            .map(({ field }) => field);

        if (subtitleFields.length) {
            return subtitleFields
                .map((field) =>
                    field.format
                        ? this.stripTags(this.renderFormat(field.format, doc))
                        : String(doc[field.key] ?? '')
                )
                .map((v) => v.trim())
                .filter(Boolean);
        }

        // Fallback: jobTitle and department if present on the document.
        return [doc.jobTitle, doc.department].filter(Boolean).map((v) => String(v));
    }

    private hasImageValue(doc: Record<string, any>): boolean {
        const field = this.fieldByRole('avatar-flag');
        const raw = field ? doc[field.key] : doc.HasImage;
        return raw === true || raw === 'true' || raw === 1;
    }

    private buildImageUrl(id: string): string {
        const base = this.siteUrl.replace(/\/$/, '');
        return `${base}/profileimages/250/${id}.jpg`; 
    }

    // ── Admin: capabilities, edit modal, remove-from-group ──────────────

    /**
     * Resolve the current user's admin flag and the active group's
     * manageability, then compute the capability map once. Behaviour/render
     * code reads `this.capabilities.*` rather than these raw facts.
     */
    private async resolveCapabilities(): Promise<void> {
        // Editing is opt-in: only attempt access checks when adminFields are
        // configured. The bulk-actions toolbar (showToolbar) is another opt-in
        // that also needs the admin flag. The group context is only required for
        // manage-scoped actions, so it must not gate the admin-status check.
        //
        // Anonymous/public pages never probe access: there is no authenticated
        // user, so we short-circuit to a non-admin, no-capability state and
        // avoid firing a request that would 401.
        if (!this.isAnonymous && (this.adminFields.length || this.showToolbar)) {
            try {
                const access = await this.userService.getAccessLevel();
                this.isAdmin = !!access?.isAdmin;
            } catch {
                this.isAdmin = false;
            }
            // Group manageability may be provided by config (admin surfaces set
            // it based on AccessLevel), defaulting to the admin flag otherwise.
            const rawManageable = (this.config as any).groupManageable;
            this.groupManageable =
                rawManageable != null ? this.checkBoolean(rawManageable) : this.isAdmin;
        }

        this.capabilities = directoryRules.capabilities<DirectoryRuleContext>({
            isAdmin: this.isAdmin,
            groupManageable: this.groupManageable,
        });
    }

    /** True when the shared bulk-actions toolbar is opted into via config. */
    private get showToolbar(): boolean {
        return this.checkBoolean((this.config as any).showToolbar, false);
    }

    /**
     * Resolve the effective group scope for the directory. Three mutually
     * exclusive modes, mirroring the legacy directory:
     *  - explicit `groupId`   → scope to that single group (highest priority).
     *  - `sharePointSiteUrl`  → resolve the EUM group(s) backing that site.
     *  - `getAllGroups: true` → scope to every group the user can see (admins)
     *                           or owns/manages (non-admins).
     *
     * The SharePoint/all-groups modes populate `scopedGroupIds`. When the
     * all-groups mode returns nothing the user has no access, so we redirect to
     * `/access-denied` just as the legacy page did. Skipped entirely for
     * anonymous pages (no authenticated context to resolve groups from).
     */
    private async resolveGroupScope(): Promise<void> {
        if (this.groupId || this.isAnonymous) return;

        const siteUrl = String(
            (this.config as any).sharePointSiteUrl ?? (this.config as any).sharepointsiteurl ?? ''
        ).trim();
        const getAllGroups = this.checkBoolean((this.config as any).getAllGroups, false);
        if (!siteUrl && !getAllGroups) return;

        // Both modes need the admin flag to choose the right endpoint/projection.
        if (!this.isAdmin) {
            try {
                const access = await this.userService.getAccessLevel();
                this.isAdmin = !!access?.isAdmin;
            } catch {
                this.isAdmin = false;
            }
        }

        try {
            const result: any = siteUrl
                ? await this.groupService.getBySharePointUrl(siteUrl)
                : await this.groupService.getAllGroups(this.isAdmin);

            // Admin searches return `{ value: [{ document }] }`; the owned/managed
            // endpoint returns `{ data: [group] }`.
            const rows: any[] = Array.isArray(result?.value)
                ? result.value.map((r: any) => r.document ?? r)
                : Array.isArray(result?.data)
                    ? result.data
                    : [];

            this.scopedGroupIds = rows
                .map((g) => String(g?.id ?? ''))
                .filter((id) => id);
        } catch {
            this.scopedGroupIds = [];
        }

        // All-groups mode with no resolved groups means no access.
        if (getAllGroups && this.scopedGroupIds.length === 0) {
            window.location.href = '/access-denied';
        }
    }

    /**
     * Fold the target group(s) into the base filter so results are scoped to the
     * group members. A single explicit `groupId` produces
     * `MemberGroups/any(g: g eq '<id>')`; a resolved SharePoint/all-groups scope
     * ORs the clause across every id. Combined (with `and`) with any existing
     * configured filter, then re-applied via rebuildFilter().
     */
    private applyGroupFilter(): void {
        const ids = this.groupId ? [this.groupId] : this.scopedGroupIds;
        if (ids.length === 0) return;

        const clause = ids
            .map((id) => `MemberGroups/any(g: g eq '${id}')`)
            .join(' or ');
        const wrapped = ids.length > 1 ? `(${clause})` : clause;

        const base = String(this.config.filter || '').trim();
        if (base.includes(wrapped)) return;
        this.config.filter = base ? `${base} and ${wrapped}` : wrapped;
        this.rebuildFilter();
    }

    // ── Admin UI injection ──────────────────────────────────────────────
    //
    // Rather than rendering admin controls up-front and hiding them, we only
    // build and insert them once the capability check authorises it. This keeps
    // the markup out of the DOM entirely for unauthorised users (defence in
    // depth) and makes the template's intent explicit for developers.

    /** True when any admin surface should exist in the DOM. */
    private get showAdminUi(): boolean {
        return this.capabilities.canEditUser;
    }

    /**
     * Inject the admin edit/confirm modals and wire their behaviour, but only
     * when the resolved capabilities permit it. No-op (and no markup) otherwise.
     */
    private maybeInjectAdminUi(): void {
        if (!this.showAdminUi) return;

        const container = this.shadow.querySelector('.eum-member-directory');
        if (!container) return;

        container.insertAdjacentHTML('beforeend', this.editModalTemplate());
        container.insertAdjacentHTML('beforeend', this.confirmModalTemplate());

        this.editModalEl = this.shadow.querySelector('.member-edit-modal');
        this.confirmModalEl = this.shadow.querySelector('.member-confirm-modal');

        if (this.editModalEl) this.modals.register(this.editModalEl);
        if (this.confirmModalEl) this.modals.register(this.confirmModalEl);

        this.bindEditModalEvents();
    }

    /** Markup for the admin edit-user modal. Only used when authorised. */
    private editModalTemplate(): string {
        // The "Remove from group" control is itself further gated: it only
        // renders when the user can manage the group *and* a group context
        // exists.
        const removeButton =
            this.capabilities.canRemoveFromGroup && this.groupId
                ? `<button type="button" class="btn btn-outline-danger edit-remove me-auto">${this.escapeHTML(this.t('memberDirectory.editModal.removeFromGroup'))}</button>`
                : '';

        return /*html*/ `
            <div class="modal fade member-edit-modal" role="dialog" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${this.escapeAttr(this.t('memberDirectory.editModal.closeLabel'))}"></button>
                        </div>
                        <div class="modal-body">
                            <div class="edit-loading">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">${this.escapeHTML(this.t('memberDirectory.editModal.loadingStatus'))}</span>
                                </div>
                            </div>
                            <form class="edit-user-form" novalidate hidden>
                                <div class="edit-layout">
                                    <div class="edit-avatar-wrap">
                                        <eum-person-avatar class="edit-avatar"></eum-person-avatar>
                                        <button type="button" class="icon-btn edit-avatar-btn" title="${this.escapeAttr(this.t('memberDirectory.editModal.changePhoto'))}" aria-label="${this.escapeAttr(this.t('memberDirectory.editModal.changePhoto'))}">
                                            ${icon('pencil')}
                                        </button>
                                        <input type="file" class="edit-avatar-input" accept="image/*" hidden>
                                    </div>
                                    <div class="edit-main">
                                        <h5 class="edit-heading mb-3">${this.escapeHTML(this.t('memberDirectory.editModal.heading'))}</h5>
                                        <div class="edit-fields"></div>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div class="modal-footer justify-content-end" hidden>
                            ${removeButton}
                            <div class="btn-group">
                                <button type="button" class="btn btn-primary edit-save">${this.escapeHTML(this.t('memberDirectory.editModal.save'))}</button>
                                <button type="button" class="btn btn-primary dropdown-toggle dropdown-toggle-split edit-save-toggle" aria-expanded="false">
                                    <span class="visually-hidden">${this.escapeHTML(this.t('memberDirectory.editModal.toggleDropdown'))}</span>
                                </button>
                                <ul class="dropdown-menu dropdown-menu-end edit-save-menu">
                                    <li><button type="button" class="dropdown-item edit-save-close">${this.escapeHTML(this.t('memberDirectory.editModal.saveAndClose'))}</button></li>
                                </ul>
                            </div>
                            <button type="button" class="btn btn-outline-secondary edit-close" data-bs-dismiss="modal">${this.escapeHTML(this.t('memberDirectory.editModal.close'))}</button>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    /** Markup for the remove-from-group confirmation modal. */
    private confirmModalTemplate(): string {
        return /*html*/ `
            <div class="modal fade member-confirm-modal" role="dialog" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered modal-sm" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">${this.escapeHTML(this.t('memberDirectory.confirmModal.title'))}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${this.escapeAttr(this.t('memberDirectory.confirmModal.closeLabel'))}"></button>
                        </div>
                        <div class="modal-body">
                            <p class="confirm-message mb-0"></p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary confirm-cancel" data-bs-dismiss="modal">${this.escapeHTML(this.t('memberDirectory.confirmModal.cancel'))}</button>
                            <button type="button" class="btn btn-danger confirm-ok">${this.escapeHTML(this.t('memberDirectory.confirmModal.confirm'))}</button>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    private bindEditModalEvents(): void {
        const modal = this.editModalEl;
        if (!modal) return;

        const avatarBtn = modal.querySelector('.edit-avatar-btn') as HTMLButtonElement | null;
        const avatarInput = modal.querySelector('.edit-avatar-input') as HTMLInputElement | null;
        avatarBtn?.addEventListener('click', () => avatarInput?.click());
        avatarInput?.addEventListener('change', () => this.handleAvatarChange(avatarInput));

        const saveBtn = modal.querySelector('.edit-save') as HTMLButtonElement | null;
        saveBtn?.addEventListener('click', () => this.saveUser(false));
        const saveCloseBtn = modal.querySelector('.edit-save-close') as HTMLButtonElement | null;
        saveCloseBtn?.addEventListener('click', () => {
            if (this.isSaving) return;
            this.saveUser(true);
        });

        const removeBtn = modal.querySelector('.edit-remove') as HTMLButtonElement | null;
        removeBtn?.addEventListener('click', () => {
            if (this.isSaving) return;
            this.confirmRemoveFromGroup();
        });

        // Bootstrap's dropdown JS doesn't auto-wire inside a shadow root, so the
        // segmented Save toggle is managed manually.
        const toggle = modal.querySelector('.edit-save-toggle') as HTMLButtonElement | null;
        const menu = modal.querySelector('.edit-save-menu') as HTMLElement | null;
        if (toggle && menu) {
            toggle.addEventListener('click', (e) => {
                e.stopPropagation();
                const open = menu.classList.toggle('show');
                toggle.setAttribute('aria-expanded', String(open));
            });
            // Close the menu on any outside click or after choosing an item.
            this.shadow.addEventListener('click', (e) => {
                if (e.target !== toggle && !toggle.contains(e.target as Node)) {
                    menu.classList.remove('show');
                    toggle.setAttribute('aria-expanded', 'false');
                }
            });
        }
    }

    /** Open the modal immediately with a spinner, then fetch and populate. */
    private async openEditModal(doc: Record<string, any>): Promise<void> {
        const modal = this.editModalEl;
        if (!modal || !this.capabilities.canEditUser) return;

        const userId = String(doc.id ?? '');
        if (!userId) return;

        const loadingEl = modal.querySelector('.edit-loading') as HTMLElement | null;
        const formEl = modal.querySelector('.edit-user-form') as HTMLElement | null;
        const footerEl = modal.querySelector('.modal-footer') as HTMLElement | null;
        const headerEl = modal.querySelector('.modal-header') as HTMLElement | null;
        const headingEl = modal.querySelector('.edit-heading') as HTMLElement | null;

        // Reset to the loading state and open immediately. The `loading` class
        // shrinks the dialog to fit just the spinner until the data arrives.
        this.editingUser = null;
        this.pendingAvatarBase64 = '';
        modal.classList.add('loading');
        if (loadingEl) loadingEl.hidden = false;
        if (formEl) formEl.hidden = true;
        if (footerEl) footerEl.hidden = true;
        if (headerEl) headerEl.hidden = true;

        const name = this.roleValue('name', doc) || String(doc.displayName ?? '');
        if (headingEl) headingEl.textContent = name
            ? this.t('memberDirectory.editModal.headingNamed', { name })
            : this.t('memberDirectory.editModal.heading');

        this.modals.show(modal);

        let user: Record<string, any>;
        try {
            user = await this.userService.getUser(userId);
        } catch {
            this.toast.error(this.t('memberDirectory.toast.loadUserFailed'));
            modal.classList.remove('loading');
            this.modals.hide(modal);
            return;
        }

        this.editingUser = user;

        const displayName = name || String(user.displayName ?? '');
        if (headingEl) headingEl.textContent = displayName
            ? this.t('memberDirectory.editModal.headingNamed', { name: displayName })
            : this.t('memberDirectory.editModal.heading');

        const avatarId = this.roleValue('avatar-id', doc) || userId;
        const hasImage = this.hasImageValue(doc);
        const imageUrl = hasImage && avatarId ? this.buildImageUrl(avatarId) : '';

        const avatar = modal.querySelector('.edit-avatar') as HTMLElement | null;
        if (avatar) {
            avatar.setAttribute('display-name', displayName);
            avatar.setAttribute('has-image', hasImage ? 'true' : 'false');
            if (imageUrl) avatar.setAttribute('image-url', imageUrl);
            else avatar.removeAttribute('image-url');
        }

        const fieldsEl = modal.querySelector('.edit-fields') as HTMLElement | null;
        if (fieldsEl) {
            fieldsEl.innerHTML = this.adminFields
                .map((field) => {
                    const value = user[field.key] ?? '';
                    const disabled = this.checkBoolean(field.readonly, false) ? ' disabled' : '';
                    return /*html*/ `
                        <div class="mb-3 edit-field">
                            <label class="form-label" for="edit-${this.escapeAttr(field.key)}">${this.escapeHTML(field.label)}</label>
                            <input type="text" class="form-control" id="edit-${this.escapeAttr(field.key)}"
                                   name="${this.escapeAttr(field.key)}" value="${this.escapeAttr(String(value))}"${disabled}>
                        </div>`;
                })
                .join('');
        }

        // Data is loaded — reveal the form and footer.
        modal.classList.remove('loading');
        if (loadingEl) loadingEl.hidden = true;
        if (formEl) formEl.hidden = false;
        if (footerEl) footerEl.hidden = false;
        if (headerEl) headerEl.hidden = false;
    }

    private handleAvatarChange(input: HTMLInputElement): void {
        const file = input.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result ?? '');
            this.pendingAvatarBase64 = result.substring(result.indexOf(',') + 1);
            const avatar = this.editModalEl?.querySelector('.edit-avatar') as HTMLElement | null;
            if (avatar) {
                avatar.setAttribute('has-image', 'true');
                avatar.setAttribute('image-url', result);
            }
        };
        reader.onerror = () => {
            this.pendingAvatarBase64 = '';
            this.toast.error(this.t('memberDirectory.toast.readImageFailed'));
        };
        reader.readAsDataURL(file);
    }

    /** PATCH only the changed fields (plus any new photo) for the user. */
    private async saveUser(closeAfter: boolean): Promise<void> {
        if (this.isSaving) return;
        const modal = this.editModalEl;
        if (!modal || !this.editingUser) return;

        const userId = String(this.editingUser.id ?? '');
        if (!userId) return;

        const payload: Record<string, unknown> = {};
        modal.querySelectorAll<HTMLInputElement>('.edit-fields input[name]').forEach((input) => {
            if (input.disabled) return;
            const key = input.name;
            const value = input.value === '' ? null : input.value;
            const original = this.editingUser![key] ?? null;
            if (value !== original) payload[key] = value;
        });

        if (this.pendingAvatarBase64) payload.photo = this.pendingAvatarBase64;

        if (Object.keys(payload).length === 0) {
            this.toast.info(this.t('memberDirectory.toast.noChanges'));
            if (closeAfter) this.modals.hide(modal);
            return;
        }

        this.setEditButtonsDisabled(true);
        try {
            await this.userService.updateUser(userId, payload);
            this.toast.success(this.t('memberDirectory.toast.updateSucceeded'));
            this.editingUser = { ...this.editingUser, ...payload };
            this.pendingAvatarBase64 = '';
            if (closeAfter) this.modals.hide(modal);
            this.refreshResults();
        } catch {
            this.toast.error(this.t('memberDirectory.toast.updateFailed'));
        } finally {
            this.setEditButtonsDisabled(false);
        }
    }

    /** Tracks whether a save request is currently in flight. */
    private isSaving = false;

    /** Enable/disable the edit modal's action buttons (used while saving). */
    private setEditButtonsDisabled(disabled: boolean): void {
        this.isSaving = disabled;
        const modal = this.editModalEl;
        if (!modal) return;
        modal
            .querySelectorAll<HTMLButtonElement>(
                '.edit-save, .edit-save-toggle, .edit-save-close, .edit-remove, .edit-close, .edit-avatar-btn'
            )
            .forEach((btn) => {
                // Set both the property and Bootstrap's `disabled` class so the
                // control is visually and behaviourally inert. `.dropdown-item`
                // and `.dropdown-toggle` only honour the class, not the property.
                btn.disabled = disabled;
                btn.classList.toggle('disabled', disabled);
                btn.setAttribute('aria-disabled', String(disabled));
                if (disabled) btn.setAttribute('tabindex', '-1');
                else btn.removeAttribute('tabindex'); 
            });
    }

    private confirmRemoveFromGroup(): void {
        const modal = this.confirmModalEl;
        if (!modal || !this.editingUser || !this.capabilities.canRemoveFromGroup) return;

        const name = String(this.editingUser.displayName ?? this.t('memberDirectory.confirmModal.fallbackName'));
        const messageEl = modal.querySelector('.confirm-message');
        if (messageEl) messageEl.textContent = this.t('memberDirectory.confirmModal.message', { name });

        const okBtn = modal.querySelector('.confirm-ok') as HTMLButtonElement | null;
        if (okBtn) {
            // Replace the handler each time to bind the current user.
            okBtn.onclick = () => this.removeFromGroup();
        }
        this.modals.show(modal);
    }

    private async removeFromGroup(): Promise<void> {
        if (!this.editingUser || !this.groupId) return;
        const userId = String(this.editingUser.id ?? '');
        if (!userId) return;

        try {
            await this.userService.removeFromGroup(userId, this.groupId);
            this.toast.success(this.t('memberDirectory.toast.removeSucceeded'));
            if (this.confirmModalEl) this.modals.hide(this.confirmModalEl);
            if (this.editModalEl) this.modals.hide(this.editModalEl);
            this.refreshResults();
        } catch {
            this.toast.error(this.t('memberDirectory.toast.removeFailed'));
        }
    }

    /** Re-run the current search so the results reflect the latest changes. */
    private refreshResults(): void {
        setTimeout(() => this.search(this.store.proxy.searchText), 400);
    }

    // ── Shared user-management toolbar ──────────────────────────────────
    //
    // The bulk-actions toolbar (add / email / copy / export) is the same shared
    // component used standalone by eum-user-management. Like the admin edit/
    // confirm modals, its markup is only injected into the DOM once the resolved
    // capabilities expose at least one action, so an unauthorised user never
    // receives it. It operates on the directory's current filtered result set.

    private mountToolbar(): void {
        if (!this.showToolbar) return;

        const capabilities: UserToolbarCapabilities = {
            canAddUsers: this.capabilities.canAddUsers,
            canEmailUsers: this.capabilities.canEmailUsers,
            canCopyUsers: this.capabilities.canCopyUsers,
            canExportUsers: this.capabilities.canExportUsers,
        };

        const anyAction =
            capabilities.canAddUsers || capabilities.canEmailUsers ||
            capabilities.canCopyUsers || capabilities.canExportUsers;
        if (!anyAction) return;

        const container = this.shadow.querySelector('.eum-member-directory') as HTMLElement | null;
        if (!container) return;

        // Inject the mount point above the search box, only now that we're
        // authorised. The directory-toolbar row is our anchor.
        const directoryToolbar = this.shadow.querySelector('.directory-toolbar');
        if (!directoryToolbar) return;
        directoryToolbar.insertAdjacentHTML('beforebegin', '<div class="management-toolbar"></div>');
        this.toolbarEl = this.shadow.querySelector('.management-toolbar');
        if (!this.toolbarEl) return;

        this.toolbar = new EumUserToolbar(this.toolbarHost(capabilities));
        this.toolbar.mount(this.toolbarEl, container);
    }

    /** The email address for a result document (resolved from configured email fields). */
    private emailOf(doc: Record<string, any>): string {
        // Prefer a field explicitly configured as `type: email`, so the address
        // comes from the mapped key (e.g. `mail`) rather than a guessed property.
        const emailField = this.memberFields.find((f) => f.type === 'email');
        if (emailField) {
            const value = doc[emailField.key];
            if (value != null && String(value).trim()) return String(value);
        }
        // Fallbacks match the legacy directory: `mail` first, then the indexed
        // `MailDirectory` copy (used when `mail` isn't projected/populated).
        return String(
            doc.email ?? doc.mail ?? doc.MailDirectory ?? doc.userPrincipalName ?? ''
        );
    }

    /** Build the host adapter the shared toolbar operates through. */
    private toolbarHost(capabilities: UserToolbarCapabilities): UserToolbarHost {
        const self = this;
        return {
            get shadow() { return self.shadow; },
            get modals() { return self.modals; },
            get prompts() { return self.prompts; },
            get toast() { return self.toast; },
            get userService() { return self.userService; },
            get groupService() { return self.groupService; },
            capabilities,
            get groupId() { return self.groupId; },
            get searchUrl() { return '/api/EUMAPI/PostObject?apiUrl=search/users%3FsearchText='; },
            get apiBaseUrl() { return String((self.config as any).apiBaseUrl ?? ''); },
            get selectFields() { return self.options.Select; },
            get orderByFields() { return self.options.OrderBy; },
            getFilter: () => self.options.searchOptions.filter || '',
            getSearchText: () => self.store.proxy.searchText,
            getCurrentResults: () => self.currentResults,
            displayName: (doc) => self.roleValue('name', doc) || String(doc.displayName ?? ''),
            emailOf: (doc) => self.emailOf(doc),
            hasImageOf: (doc) => self.hasImageValue(doc),
            avatarImageUrlOf: (doc) => {
                const avatarId = self.roleValue('avatar-id', doc);
                return self.hasImageValue(doc) && avatarId ? self.buildImageUrl(avatarId) : '';
            },
            escapeHTML: (str) => self.escapeHTML(str),
            escapeAttr: (str) => self.escapeAttr(str),
            refreshResults: () => self.refreshResults(),
        };
    }

    // ── Details modal ───────────────────────────────────────────────────

    private openDetailsModal(doc: Record<string, any>): void {
        const modal = this.detailsModalEl;
        if (!modal) return;

        const name = this.roleValue('name', doc);
        const subtitles = this.subtitleValues(doc);
        const avatarId = this.roleValue('avatar-id', doc);
        const hasImage = this.hasImageValue(doc);
        const imageUrl = hasImage && avatarId ? this.buildImageUrl(avatarId) : '';

        const avatar = modal.querySelector('.details-avatar') as HTMLElement | null;
        if (avatar) {
            avatar.setAttribute('display-name', name);
            avatar.setAttribute('has-image', hasImage ? 'true' : 'false');
            if (imageUrl) avatar.setAttribute('image-url', imageUrl);
            else avatar.removeAttribute('image-url');
        }

        const nameEl = modal.querySelector('.details-name');
        if (nameEl) nameEl.textContent = name;

        const subtitleEl = modal.querySelector('.details-subtitle') as HTMLElement | null;
        if (subtitleEl) {
            subtitleEl.innerHTML = subtitles
                .map((s) => `<div>${this.escapeHTML(s)}</div>`)
                .join('');
            subtitleEl.hidden = subtitles.length === 0;
        }

        // Contact/social icons row — same icon-type fields used on the cards.
        const iconsEl = modal.querySelector('.details-icons') as HTMLElement | null;
        if (iconsEl) {
            const iconMarkup = this.fieldsFor('details')
                .filter((f) => f.type && f.type !== 'text')
                .map((f) => this.renderIcon(f, doc))
                .filter(Boolean)
                .join('');
            iconsEl.innerHTML = iconMarkup;
            iconsEl.hidden = !iconMarkup;
        }

        // Full field list — presentation governed by `detailsMode`:
        //  - simple: never rendered (identity + icons only).
        //  - full:   rendered and expanded on open, with no toggle.
        //  - toggle: rendered but hidden until "More Details" is toggled.
        const mode = this.detailsMode;
        const contentEl = modal.querySelector('.details-content') as HTMLElement | null;
        const fieldsEl = modal.querySelector('.details-fields') as HTMLElement | null;
        let fieldsMarkup = '';
        if (fieldsEl && mode !== 'simple') {
            const keepEmpty = this.checkBoolean((this.config as any).keepEmptyDetails, true);
            fieldsMarkup = this.fieldsFor('details')
                .map((f) => {
                    const value = this.renderFieldValue(f, doc);
                    const hasValue = !!this.stripTags(value).trim();
                    if (!hasValue && !keepEmpty) return '';
                    return /*html*/ `
                        <div class="details-field">
                            <div class="details-field-label">${this.escapeHTML(f.label)}</div>
                            <div class="details-field-value">${hasValue ? value : '&nbsp;'}</div>
                        </div>`;
                })
                .join('');
            fieldsEl.innerHTML = fieldsMarkup;
            const detailCols = this.columnCount('details');
            if (detailCols) fieldsEl.style.setProperty('--grid-column-count', String(detailCols));
            else fieldsEl.style.removeProperty('--grid-column-count');
        } else if (fieldsEl) {
            // Simple mode: ensure no stale field markup lingers from a prior open.
            fieldsEl.innerHTML = '';
        }
        // In `full` mode the field list opens expanded; otherwise it starts hidden.
        if (contentEl) contentEl.hidden = !(mode === 'full' && !!fieldsMarkup);

        // Edit User (admin-gated) — placed before "View Profile". The button
        // only exists in the DOM when the user is authorised to edit; there is
        // no hidden fallback for unauthorised users.
        if (this.showAdminUi && doc.id) {
            const footer = modal.querySelector('.modal-footer');
            let editBtn = modal.querySelector('.details-edit') as HTMLButtonElement | null;
            if (!editBtn && footer) {
                editBtn = document.createElement('button');
                editBtn.type = 'button';
                editBtn.className = 'details-edit btn';
                footer.insertBefore(editBtn, footer.firstChild);
            }
            if (editBtn) {
                editBtn.innerHTML = `${icon('pencil')}<span>${this.escapeHTML(this.t('memberDirectory.detailsModal.editUser'))}</span>`;
                editBtn.onclick = () => {
                    this.hideDetailsModal();
                    this.openEditModal(doc);
                };
            }
        }

        // Remove from group (admin-gated) — placed to the right of "Edit User".
        // Gated identically to the edit modal's remove control: it only renders
        // when the user can manage the group *and* a group context exists.
        if (this.showAdminUi && doc.id && this.capabilities.canRemoveFromGroup && this.groupId) {
            const footer = modal.querySelector('.modal-footer');
            const editBtn = modal.querySelector('.details-edit');
            let removeBtn = modal.querySelector('.details-remove') as HTMLButtonElement | null;
            if (!removeBtn && footer) {
                removeBtn = document.createElement('button');
                removeBtn.type = 'button';
                removeBtn.className = 'details-remove btn';
                // Insert directly after the Edit User button when present,
                // otherwise at the start of the footer.
                if (editBtn && editBtn.nextSibling) footer.insertBefore(removeBtn, editBtn.nextSibling);
                else if (editBtn) footer.appendChild(removeBtn);
                else footer.insertBefore(removeBtn, footer.firstChild);
            }
            if (removeBtn) {
                removeBtn.innerHTML = `${icon('person-dash')}<span>${this.escapeHTML(this.t('memberDirectory.detailsModal.removeUser'))}</span>`;
                removeBtn.onclick = () => {
                    // The shared confirm flow removes `this.editingUser`, so seed
                    // it from the current details record.
                    this.editingUser = doc;
                    this.hideDetailsModal();
                    this.confirmRemoveFromGroup();
                };
            }
        }

        const profileLink = modal.querySelector('.details-profile-link') as HTMLAnchorElement | null;
        if (profileLink) {
            const url = this.buildProfileUrl(doc);
            if (url) {
                profileLink.href = url;
                profileLink.innerHTML = `${icon('person-circle')}<span>${this.escapeHTML(this.t('memberDirectory.detailsModal.viewProfile'))}</span>`;
                profileLink.hidden = false;
            } else {
                profileLink.removeAttribute('href');
                profileLink.hidden = true;
            }
        }

        // "More Details" toggles the full field list. Only offered in `toggle`
        // mode (and only when there are fields); `full`/`simple` have no toggle.
        const moreBtn = modal.querySelector('.details-more') as HTMLButtonElement | null;
        if (moreBtn) {
            if (mode === 'toggle' && fieldsMarkup) {
                moreBtn.hidden = false;
                moreBtn.setAttribute('aria-expanded', 'false');
                moreBtn.innerHTML = `${icon('arrow-up-right-square')}<span>${this.escapeHTML(this.t('memberDirectory.detailsModal.moreDetails'))}</span>`;
                moreBtn.onclick = () => {
                    const showing = contentEl ? !contentEl.hidden : false;
                    if (contentEl) contentEl.hidden = showing;
                    modal.classList.toggle('detailed', !showing);
                    moreBtn.setAttribute('aria-expanded', String(!showing));
                    moreBtn.innerHTML = showing
                        ? `${icon('arrow-up-right-square')}<span>${this.escapeHTML(this.t('memberDirectory.detailsModal.moreDetails'))}</span>`
                        : `${icon('arrow-down-left-square')}<span>${this.escapeHTML(this.t('memberDirectory.detailsModal.fewerDetails'))}</span>`;
                };
            } else {
                moreBtn.hidden = true;
                moreBtn.onclick = null;
            }
        }

        // Open expanded in `full` mode (when fields exist); collapsed otherwise.
        modal.classList.toggle('detailed', mode === 'full' && !!fieldsMarkup);

        this.showDetailsModal();
    }

    private buildProfileUrl(doc: Record<string, any>): string {
        const field = this.fieldByRole('profile-url');
        const raw = field ? doc[field.key] : (doc.Slug ?? doc.id);
        if (!raw || !this.profileUrl) return '';
        const value = decodeURIComponent(String(raw));
        return `${this.profileUrl}?user=${encodeURIComponent(value)}`;
    }

    private showDetailsModal(): void {
        if (this.detailsModalEl) this.modals.show(this.detailsModalEl);
    }

    private hideDetailsModal(): void {
        if (this.detailsModalEl) this.modals.hide(this.detailsModalEl);
    }

    private renderFieldValue(field: MemberFieldConfig, doc: Record<string, any>, withIcon = true): string {
        if (field.type && field.type !== 'text') {
            const link = this.renderIcon(field, doc, /*withText*/ true, withIcon);
            if (link) return link;
        }
        const text = field.format
            ? this.renderFormat(field.format, doc)
            : this.escapeHTML(String(doc[field.key] ?? ''));
        if (!this.stripTags(text).trim()) return text;
        const iconMarkup = withIcon && field.icon && hasIcon(field.icon) ? icon(field.icon, { title: field.label }) : '';
        return iconMarkup
            ? `${iconMarkup}<span class="value-text">${text}</span>`
            : text;
    }

    private renderIcon(field: MemberFieldConfig, doc: Record<string, any>, withText = false, withIcon = true): string {
        const raw = doc[field.key];
        if (raw === undefined || raw === null || raw === '') return '';
        const value = String(raw);
        const iconMarkup = withIcon && field.icon && hasIcon(field.icon) ? icon(field.icon, { title: field.label }) : '';
        const label = withText ? `<span class="value-text">${this.escapeHTML(value)}</span>` : '';
        const title = this.escapeAttr(field.label || value);

        let href = '';
        let target = '';
        switch (field.type) {
            case 'email':
                href = `mailto:${value}`;
                break;
            case 'phone':
                href = `tel:${value}`;
                break;
            case 'chat':
                href = `https://teams.microsoft.com/l/chat/0/0?users=${value}`;
                target = ' target="_blank" rel="noopener"';
                break;
            case 'link':
                href = value;
                target = ' target="_blank" rel="noopener"';
                break;
            default:
                return withText ? this.escapeHTML(value) : '';
        }

        return `<a href="${this.escapeAttr(href)}" title="${title}"${target}>${iconMarkup}${label ? `${label}` : ''}</a>`;
    }

    private escapeAttr(str: string): string {
        return this.escapeHTML(str).replace(/"/g, '&quot;');
    }

    private stripTags(html: string): string {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent ?? '';
    }
}

customElements.define('eum-member-directory', EumMemberDirectory);
